import { supabase } from '@/integrations/supabase/client';
import { logError } from '@/lib/error-handler';

export interface EmailAccount {
  id: string;
  user_id: string;
  email_address: string;
  provider: 'gmail' | 'outlook' | 'exchange' | 'imap';
  is_primary: boolean;
  is_active: boolean;
  sync_status: 'connected' | 'disconnected' | 'syncing' | 'error';
  last_sync: string;
  settings: {
    sync_sent: boolean;
    sync_received: boolean;
    sync_frequency: number; // minutes
    auto_track: boolean;
    signature: string;
  };
  oauth_token?: string;
  oauth_refresh_token?: string;
  created_at: string;
}

export interface EmailMessage {
  id: string;
  email_account_id: string;
  message_id: string;
  thread_id?: string;
  from_email: string;
  from_name?: string;
  to_emails: string[];
  cc_emails?: string[];
  bcc_emails?: string[];
  subject: string;
  body_text?: string;
  body_html?: string;
  sent_at: string;
  received_at?: string;
  is_outbound: boolean;
  is_read: boolean;
  has_attachments: boolean;
  labels: string[];
  contact_id?: string;
  organization_id?: string;
  opportunity_id?: string;
  activity_id?: string;
  sync_status: 'pending' | 'synced' | 'error';
  created_at: string;
}

export interface CalendarAccount {
  id: string;
  user_id: string;
  provider: 'google' | 'outlook' | 'exchange';
  calendar_id: string;
  calendar_name: string;
  is_primary: boolean;
  is_active: boolean;
  sync_status: 'connected' | 'disconnected' | 'syncing' | 'error';
  last_sync: string;
  settings: {
    sync_frequency: number; // minutes
    auto_create_activities: boolean;
    default_activity_type: string;
  };
  oauth_token?: string;
  oauth_refresh_token?: string;
  created_at: string;
}

export interface CalendarEvent {
  id: string;
  calendar_account_id: string;
  event_id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  all_day: boolean;
  location?: string;
  attendees: {
    email: string;
    name?: string;
    response_status: 'needsAction' | 'declined' | 'tentative' | 'accepted';
  }[];
  organizer_email: string;
  organizer_name?: string;
  is_recurring: boolean;
  recurrence_rule?: string;
  contact_id?: string;
  organization_id?: string;
  opportunity_id?: string;
  activity_id?: string;
  sync_status: 'pending' | 'synced' | 'error';
  created_at: string;
  updated_at: string;
}

export interface UnifiedMessage {
  id: string;
  type: 'email' | 'calendar' | 'call' | 'meeting';
  from: string;
  to: string[];
  subject: string;
  content?: string;
  timestamp: string;
  contact_id?: string;
  organization_id?: string;
  opportunity_id?: string;
  metadata: Record<string, any>;
}

class CommunicationHubService {
  // Email Account Management
  async connectEmailAccount(
    accountData: Omit<EmailAccount, 'id' | 'created_at' | 'last_sync'>
  ): Promise<EmailAccount> {
    const { data, error } = await supabase
      .from('email_accounts')
      .insert({
        ...accountData,
        last_sync: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getEmailAccounts(userId: string): Promise<EmailAccount[]> {
    const { data, error } = await supabase
      .from('email_accounts')
      .select('*')
      .eq('user_id', userId)
      .order('is_primary', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async updateEmailAccount(id: string, updates: Partial<EmailAccount>): Promise<EmailAccount> {
    const { data, error } = await supabase
      .from('email_accounts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async disconnectEmailAccount(id: string): Promise<void> {
    const { error } = await supabase
      .from('email_accounts')
      .update({
        is_active: false,
        sync_status: 'disconnected',
        oauth_token: null,
        oauth_refresh_token: null,
      })
      .eq('id', id);

    if (error) throw error;
  }

  // Email Sync
  async syncEmails(accountId: string): Promise<void> {
    try {
      // Update sync status
      await this.updateEmailAccount(accountId, {
        sync_status: 'syncing',
        last_sync: new Date().toISOString(),
      });

      // Get account details
      const { data: account } = await supabase
        .from('email_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (!account) throw new Error('Email account not found');

      // Sync based on provider
      switch (account.provider) {
        case 'gmail':
          await this.syncGmailEmails(account);
          break;
        case 'outlook':
          await this.syncOutlookEmails(account);
          break;
        case 'exchange':
          await this.syncExchangeEmails(account);
          break;
        case 'imap':
          await this.syncImapEmails(account);
          break;
      }

      // Update sync status
      await this.updateEmailAccount(accountId, {
        sync_status: 'connected',
        last_sync: new Date().toISOString(),
      });
    } catch (error) {
      logError('Email sync failed:', error);
      await this.updateEmailAccount(accountId, { sync_status: 'error' });
      throw error;
    }
  }

  private async syncGmailEmails(account: EmailAccount): Promise<void> {
    // Gmail API integration would go here
    // For demo purposes, this is a placeholder
    // Mock sync - in production this would use Gmail API
    const mockEmails = this.generateMockEmails(account.id, 5);

    for (const email of mockEmails) {
      await this.saveEmailMessage(email);
    }
  }

  private async syncOutlookEmails(account: EmailAccount): Promise<void> {
    // Outlook API integration would go here
    const mockEmails = this.generateMockEmails(account.id, 3);
    for (const email of mockEmails) {
      await this.saveEmailMessage(email);
    }
  }

  private async syncExchangeEmails(account: EmailAccount): Promise<void> {
    // Exchange Web Services integration would go here
  }

  private async syncImapEmails(account: EmailAccount): Promise<void> {
    // IMAP integration would go here
  }

  private generateMockEmails(
    accountId: string,
    count: number
  ): Omit<EmailMessage, 'id' | 'created_at'>[] {
    const emails = [];
    for (let i = 0; i < count; i++) {
      emails.push({
        email_account_id: accountId,
        message_id: `mock-${Date.now()}-${i}`,
        from_email: 'client@example.com',
        from_name: 'John Client',
        to_emails: ['you@company.com'],
        subject: `Follow up on proposal ${i + 1}`,
        body_text: 'Thank you for the proposal. We are reviewing it and will get back to you soon.',
        body_html:
          '<p>Thank you for the proposal. We are reviewing it and will get back to you soon.</p>',
        sent_at: new Date(Date.now() - Math.random() * 86400000).toISOString(), // Random time in last 24 hours
        is_outbound: false,
        is_read: Math.random() > 0.5,
        has_attachments: Math.random() > 0.7,
        labels: ['inbox'],
        sync_status: 'synced' as const,
      });
    }
    return emails;
  }

  private async saveEmailMessage(
    emailData: Omit<EmailMessage, 'id' | 'created_at'>
  ): Promise<EmailMessage> {
    // Check if message already exists
    const { data: existing } = await supabase
      .from('email_messages')
      .select('id')
      .eq('message_id', emailData.message_id)
      .single();

    if (existing) {
      // Update existing message
      const { data, error } = await supabase
        .from('email_messages')
        .update(emailData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      // Create new message
      const { data, error } = await supabase
        .from('email_messages')
        .insert(emailData)
        .select()
        .single();

      if (error) throw error;

      // Auto-link to contacts/organizations if possible
      await this.autoLinkEmailMessage(data);

      return data;
    }
  }

  private async autoLinkEmailMessage(email: EmailMessage): Promise<void> {
    try {
      // Try to find matching contact by email
      const { data: contact } = await supabase
        .from('contacts')
        .select('id, organization_id')
        .eq('email', email.from_email)
        .single();

      if (contact) {
        await supabase
          .from('email_messages')
          .update({
            contact_id: contact.id,
            organization_id: contact.organization_id,
          })
          .eq('id', email.id);

        // Create activity record
        await supabase.from('activities').insert({
          type: 'email',
          description: `Email: ${email.subject}`,
          contact_id: contact.id,
          organization_id: contact.organization_id,
          metadata: {
            email_id: email.id,
            email_subject: email.subject,
            is_outbound: email.is_outbound,
          },
        });
      }
    } catch (error) {
      logError('Failed to auto-link email:', error);
    }
  }

  // Calendar Account Management
  async connectCalendarAccount(
    accountData: Omit<CalendarAccount, 'id' | 'created_at' | 'last_sync'>
  ): Promise<CalendarAccount> {
    const { data, error } = await supabase
      .from('calendar_accounts')
      .insert({
        ...accountData,
        last_sync: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getCalendarAccounts(userId: string): Promise<CalendarAccount[]> {
    const { data, error } = await supabase
      .from('calendar_accounts')
      .select('*')
      .eq('user_id', userId)
      .order('is_primary', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async syncCalendar(accountId: string): Promise<void> {
    try {
      await this.updateCalendarAccount(accountId, {
        sync_status: 'syncing',
        last_sync: new Date().toISOString(),
      });

      const { data: account } = await supabase
        .from('calendar_accounts')
        .select('*')
        .eq('id', accountId)
        .single();

      if (!account) throw new Error('Calendar account not found');

      switch (account.provider) {
        case 'google':
          await this.syncGoogleCalendar(account);
          break;
        case 'outlook':
          await this.syncOutlookCalendar(account);
          break;
        case 'exchange':
          await this.syncExchangeCalendar(account);
          break;
      }

      await this.updateCalendarAccount(accountId, {
        sync_status: 'connected',
        last_sync: new Date().toISOString(),
      });
    } catch (error) {
      logError('Calendar sync failed:', error);
      await this.updateCalendarAccount(accountId, { sync_status: 'error' });
      throw error;
    }
  }

  private async updateCalendarAccount(
    id: string,
    updates: Partial<CalendarAccount>
  ): Promise<CalendarAccount> {
    const { data, error } = await supabase
      .from('calendar_accounts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  private async syncGoogleCalendar(account: CalendarAccount): Promise<void> {
    // Mock events for demo
    const mockEvents = this.generateMockEvents(account.id, 3);
    for (const event of mockEvents) {
      await this.saveCalendarEvent(event);
    }
  }

  private async syncOutlookCalendar(account: CalendarAccount): Promise<void> {
    const mockEvents = this.generateMockEvents(account.id, 2);
    for (const event of mockEvents) {
      await this.saveCalendarEvent(event);
    }
  }

  private async syncExchangeCalendar(account: CalendarAccount): Promise<void> {}

  private generateMockEvents(
    accountId: string,
    count: number
  ): Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>[] {
    const events = [];
    for (let i = 0; i < count; i++) {
      const startTime = new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000); // Next 7 days
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 hour duration

      events.push({
        calendar_account_id: accountId,
        event_id: `mock-event-${Date.now()}-${i}`,
        title: `Client Meeting ${i + 1}`,
        description: 'Quarterly business review meeting',
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        all_day: false,
        location: 'Conference Room A',
        attendees: [
          {
            email: 'client@example.com',
            name: 'John Client',
            response_status: 'accepted',
          },
        ],
        organizer_email: 'you@company.com',
        organizer_name: 'You',
        is_recurring: false,
        sync_status: 'synced' as const,
      });
    }
    return events;
  }

  private async saveCalendarEvent(
    eventData: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at'>
  ): Promise<CalendarEvent> {
    const { data: existing } = await supabase
      .from('calendar_events')
      .select('id')
      .eq('event_id', eventData.event_id)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('calendar_events')
        .update({
          ...eventData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('calendar_events')
        .insert(eventData)
        .select()
        .single();

      if (error) throw error;

      await this.autoLinkCalendarEvent(data);
      return data;
    }
  }

  private async autoLinkCalendarEvent(event: CalendarEvent): Promise<void> {
    try {
      // Find contacts based on attendee emails
      const attendeeEmails = event.attendees.map((a) => a.email);

      const { data: contacts } = await supabase
        .from('contacts')
        .select('id, organization_id, email')
        .in('email', attendeeEmails);

      if (contacts && contacts.length > 0) {
        const primaryContact = contacts[0];

        await supabase
          .from('calendar_events')
          .update({
            contact_id: primaryContact.id,
            organization_id: primaryContact.organization_id,
          })
          .eq('id', event.id);

        // Create activity if settings allow
        const { data: account } = await supabase
          .from('calendar_accounts')
          .select('settings')
          .eq('id', event.calendar_account_id)
          .single();

        if (account?.settings.auto_create_activities) {
          await supabase.from('activities').insert({
            type: account.settings.default_activity_type || 'meeting',
            description: `Meeting: ${event.title}`,
            contact_id: primaryContact.id,
            organization_id: primaryContact.organization_id,
            scheduled_at: event.start_time,
            metadata: {
              calendar_event_id: event.id,
              location: event.location,
              attendees: event.attendees,
            },
          });
        }
      }
    } catch (error) {
      logError('Failed to auto-link calendar event:', error);
    }
  }

  // Unified Communication Timeline
  async getUnifiedTimeline(filters: {
    contact_id?: string;
    organization_id?: string;
    opportunity_id?: string;
    start_date?: string;
    end_date?: string;
    types?: ('email' | 'calendar' | 'call' | 'meeting')[];
    limit?: number;
  }): Promise<UnifiedMessage[]> {
    const messages: UnifiedMessage[] = [];

    // Get emails
    if (!filters.types || filters.types.includes('email')) {
      const emailQuery = supabase.from('email_messages').select('*');

      if (filters.contact_id) emailQuery.eq('contact_id', filters.contact_id);
      if (filters.organization_id) emailQuery.eq('organization_id', filters.organization_id);
      if (filters.start_date) emailQuery.gte('sent_at', filters.start_date);
      if (filters.end_date) emailQuery.lte('sent_at', filters.end_date);

      const { data: emails } = await emailQuery
        .order('sent_at', { ascending: false })
        .limit(filters.limit || 50);

      if (emails) {
        messages.push(
          ...emails.map((email) => ({
            id: email.id,
            type: 'email' as const,
            from: email.from_email,
            to: email.to_emails,
            subject: email.subject,
            content: email.body_text,
            timestamp: email.sent_at,
            contact_id: email.contact_id,
            organization_id: email.organization_id,
            opportunity_id: email.opportunity_id,
            metadata: {
              message_id: email.message_id,
              is_outbound: email.is_outbound,
              has_attachments: email.has_attachments,
            },
          }))
        );
      }
    }

    // Get calendar events
    if (!filters.types || filters.types.includes('calendar')) {
      const calendarQuery = supabase.from('calendar_events').select('*');

      if (filters.contact_id) calendarQuery.eq('contact_id', filters.contact_id);
      if (filters.organization_id) calendarQuery.eq('organization_id', filters.organization_id);
      if (filters.start_date) calendarQuery.gte('start_time', filters.start_date);
      if (filters.end_date) calendarQuery.lte('start_time', filters.end_date);

      const { data: events } = await calendarQuery
        .order('start_time', { ascending: false })
        .limit(filters.limit || 50);

      if (events) {
        messages.push(
          ...events.map((event) => ({
            id: event.id,
            type: 'calendar' as const,
            from: event.organizer_email,
            to: event.attendees.map((a) => a.email),
            subject: event.title,
            content: event.description,
            timestamp: event.start_time,
            contact_id: event.contact_id,
            organization_id: event.organization_id,
            opportunity_id: event.opportunity_id,
            metadata: {
              event_id: event.event_id,
              location: event.location,
              end_time: event.end_time,
              attendees: event.attendees,
            },
          }))
        );
      }
    }

    // Sort by timestamp
    return messages
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, filters.limit || 50);
  }

  // Email Sending
  async sendEmail(emailData: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body_html: string;
    body_text?: string;
    account_id?: string;
    contact_id?: string;
    organization_id?: string;
    opportunity_id?: string;
    template_id?: string;
  }): Promise<EmailMessage> {
    try {
      // Send email through provider (Gmail, Outlook, etc.)
      // For demo purposes, we'll just create a record

      const emailMessage: Omit<EmailMessage, 'id' | 'created_at'> = {
        email_account_id: emailData.account_id || 'default',
        message_id: `sent-${Date.now()}`,
        from_email: 'you@company.com', // Would come from account
        from_name: 'Your Name',
        to_emails: emailData.to,
        cc_emails: emailData.cc,
        bcc_emails: emailData.bcc,
        subject: emailData.subject,
        body_html: emailData.body_html,
        body_text: emailData.body_text || this.htmlToText(emailData.body_html),
        sent_at: new Date().toISOString(),
        is_outbound: true,
        is_read: true, // Sent emails are considered "read"
        has_attachments: false,
        labels: ['sent'],
        contact_id: emailData.contact_id,
        organization_id: emailData.organization_id,
        opportunity_id: emailData.opportunity_id,
        sync_status: 'synced',
      };

      const { data, error } = await supabase
        .from('email_messages')
        .insert(emailMessage)
        .select()
        .single();

      if (error) throw error;

      // Create activity record
      if (emailData.contact_id) {
        await supabase.from('activities').insert({
          type: 'email',
          description: `Sent email: ${emailData.subject}`,
          contact_id: emailData.contact_id,
          organization_id: emailData.organization_id,
          opportunity_id: emailData.opportunity_id,
          metadata: {
            email_id: data.id,
            is_outbound: true,
          },
        });
      }

      return data;
    } catch (error) {
      logError('Failed to send email:', error);
      throw error;
    }
  }

  private htmlToText(html: string): string {
    // Simple HTML to text conversion (in production use a proper library)
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
  }

  // Analytics
  async getCommunicationAnalytics(timeframe: 'day' | 'week' | 'month' = 'week'): Promise<any> {
    const startDate = new Date();
    switch (timeframe) {
      case 'day':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
    }

    const [emails, events] = await Promise.all([
      supabase.from('email_messages').select('*').gte('sent_at', startDate.toISOString()),
      supabase.from('calendar_events').select('*').gte('start_time', startDate.toISOString()),
    ]);

    const emailData = emails.data || [];
    const eventData = events.data || [];

    return {
      email_stats: {
        total_emails: emailData.length,
        sent_emails: emailData.filter((e) => e.is_outbound).length,
        received_emails: emailData.filter((e) => !e.is_outbound).length,
        unread_emails: emailData.filter((e) => !e.is_read).length,
        response_rate: this.calculateResponseRate(emailData),
      },
      calendar_stats: {
        total_events: eventData.length,
        upcoming_events: eventData.filter((e) => new Date(e.start_time) > new Date()).length,
        past_events: eventData.filter((e) => new Date(e.start_time) < new Date()).length,
      },
      activity_by_day: this.groupActivityByDay([...emailData, ...eventData]),
    };
  }

  private calculateResponseRate(emails: EmailMessage[]): number {
    const sentEmails = emails.filter((e) => e.is_outbound);
    if (sentEmails.length === 0) return 0;

    const responseMap = new Map<string, boolean>();

    sentEmails.forEach((email) => {
      const key = email.thread_id || email.subject;
      responseMap.set(key, false);
    });

    emails
      .filter((e) => !e.is_outbound)
      .forEach((email) => {
        const key = email.thread_id || email.subject;
        if (responseMap.has(key)) {
          responseMap.set(key, true);
        }
      });

    const responses = Array.from(responseMap.values()).filter(Boolean).length;
    return Math.round((responses / sentEmails.length) * 100);
  }

  private groupActivityByDay(activities: any[]): Record<string, number> {
    return activities.reduce((acc, activity) => {
      const date = (activity.sent_at || activity.start_time).split('T')[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});
  }
}

export const communicationHubService = new CommunicationHubService();
